import { useState, useEffect, useCallback, useMemo } from 'react';
import { Contracts, useContract } from './useContract';
import { FuturaDropStage, FUTURA_MINT_MIX_GAS_LIMIT } from '../constants';
import { futuraChainId, futuraContractAddress, remixPaymentToken } from 'utils/config';
import { Futura } from 'contracts';
import { calculateGasMargin } from 'utils/transactions';
import { ERC20Token, OwnedNft } from 'utils/types';
import { useAllOwnedNfts } from './useEbApi';
import { mixpassToken } from 'components/Mixpass/mixpassConfig';
import { verifyAddressesMatch } from 'utils/ethAddress';

const CLIP_IDS_ALREADY_TAKEN = 'execution reverted: Clip IDs already taken';

interface UseFuturaResult {
  dropStage: FuturaDropStage | null;
  checkRemix: (
    tokenIdLeft: number,
    tokenIdRight: number,
    bpmIndex: number,
    clipIds: number[],
    cb?: any
  ) => any;
  mintRemix: (
    tokenIdLeft: number,
    tokenIdRight: number,
    bpmIndex: number,
    clipIds: number[],
    burnA: boolean,
    burnB: boolean,
    cb?: any
  ) => any;
  mintMixes: (count: number, cb?: any) => any;
  paymentToken: ERC20Token;
}

const useFutura = (): UseFuturaResult => {
  const [dropStage, setDropStage] = useState(null);
  const contract = useContract(futuraContractAddress, Contracts.Futura, futuraChainId);

  useEffect(() => {
    const getStage = async () => {
      const stage = await contract!.dropStage();
      setDropStage(stage);
    };
    if (contract) {
      getStage();
    }
  }, [contract]);

  const mintMixes = useCallback(
    async (count, cb?: any) => {
      if (Boolean(contract)) {
        try {
          const res = await contract!.mintManyMixes(count, {
            gasLimit: count * FUTURA_MINT_MIX_GAS_LIMIT,
          });
          return res;
        } catch (e) {
          console.error('Failed to mint mix', e);
          throw e;
        }
      }
    },
    [contract]
  );

  const checkRemix = useCallback(
    async (tokenIdLeft: number, tokenIdRight: number, bpmIndex: number, clipIds: number[]) => {
      if (Boolean(contract)) {
        try {
          await (contract as Futura)!.estimateGas.mintRemixWithTwoBurns(
            tokenIdLeft,
            tokenIdRight,
            bpmIndex,
            clipIds
          );
          return true;
        } catch (e: any) {
          if (e.data.message === CLIP_IDS_ALREADY_TAKEN) {
            return false;
          }
          throw e;
        }
      } else {
        console.log('no contract');
        return false;
      }
    },
    [contract]
  );

  const mintRemix = useCallback(
    async (
      tokenIdLeft: number,
      tokenIdRight: number,
      bpmIndex: number,
      clipIds: number[],
      burnLeft: boolean,
      burnRight: boolean
    ) => {
      console.log({ tokenIdLeft, tokenIdRight, bpmIndex, clipIds, burnLeft, burnRight });
      if (Boolean(contract)) {
        if (!burnLeft && !burnRight) {
          let gasEstimate = await (contract as Futura)!.estimateGas.mintRemixWithNoBurn(
            tokenIdLeft,
            tokenIdRight,
            bpmIndex,
            clipIds
          );
          return (contract as Futura)!.mintRemixWithNoBurn(
            tokenIdLeft,
            tokenIdRight,
            bpmIndex,
            clipIds,
            {
              gasLimit: calculateGasMargin(gasEstimate, 2000000),
            }
          );
        } else if (burnLeft && burnRight) {
          let gasEstimate = await (contract as Futura)!.estimateGas.mintRemixWithTwoBurns(
            tokenIdLeft,
            tokenIdRight,
            bpmIndex,
            clipIds
          );
          return (contract as Futura)!.mintRemixWithTwoBurns(
            tokenIdLeft,
            tokenIdRight,
            bpmIndex,
            clipIds,
            {
              gasLimit: calculateGasMargin(gasEstimate, 2000000),
            }
          );
        } else if (burnLeft && !burnRight) {
          let gasEstimate = await (contract as Futura)!.estimateGas.mintRemixWithOneBurn(
            tokenIdRight,
            tokenIdLeft,
            bpmIndex,
            clipIds
          );
          return (contract as Futura)!.mintRemixWithOneBurn(
            tokenIdRight,
            tokenIdLeft,
            bpmIndex,
            clipIds,
            {
              gasLimit: calculateGasMargin(gasEstimate, 2000000),
            }
          );
        } else if (!burnLeft && burnRight) {
          let gasEstimate = await (contract as Futura)!.estimateGas.mintRemixWithOneBurn(
            tokenIdLeft,
            tokenIdRight,
            bpmIndex,
            clipIds
          );
          return (contract as Futura)!.mintRemixWithOneBurn(
            tokenIdLeft,
            tokenIdRight,
            bpmIndex,
            clipIds,
            {
              gasLimit: calculateGasMargin(gasEstimate, 2000000),
            }
          );
        }
      }
    },
    [contract]
  );

  const paymentToken = {
    address: remixPaymentToken,
    decimals: 18,
    symbol: 'WETH',
  };

  return {
    mintMixes,
    checkRemix,
    mintRemix,
    dropStage,
    paymentToken,
  };
};

export default useFutura;

export const useOwnedFuturaNfts = (address: string | null, queryConfig: any = {}) => {
  const { data, ...rest } = useAllOwnedNfts(
    address,
    [futuraChainId],
    [futuraContractAddress, mixpassToken.address],
    queryConfig
  );

  // TODO: maybe use status if this is re-rendering too much
  const [mix, mixpass] = useMemo(() => {
    const mix: OwnedNft[] = [];
    const mixpass: OwnedNft[] = [];

    data?.forEach(nft => {
      if (nft.contract_address === futuraContractAddress && nft.metadata) {
        mix.push(nft);
        // use helper that checksums addresses
      } else if (verifyAddressesMatch(nft.contract_address, mixpassToken.address)) {
        mixpass.push(nft);
      }
    });

    return [mix, mixpass];
  }, [data]);

  const ownsMixpass = Boolean(mixpass?.length && parseInt(mixpass[0]?.quantity ?? 0, 10) > 0);

  return {
    ...rest,
    data,
    mixpass: mixpass || [],
    mix: mix || [],
    ownsMixpass,
  };
};
